import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Empty,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Select,
  Space,
  Spin,
  Switch,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  ReloadOutlined,
  SaveOutlined,
} from '@ant-design/icons';
import { getServiceCategoryTree, type ServiceCategory } from '../../services/serviceCategoryService';
import {
  getServiceProcessConfig,
  saveServiceProcessConfig,
  type ServiceProcessStep,
  type ServiceProcessUploadRequirement,
} from '../../services/serviceProcessConfigService';

const { Paragraph, Text, Title } = Typography;

interface CategoryOption {
  label: string;
  value: string;
  disabled?: boolean;
}

const uploadRequirementOptions: { label: string; value: ServiceProcessUploadRequirement }[] = [
  { label: '不要求上传', value: 'none' },
  { label: '必须上传图片', value: 'image' },
  { label: '必须上传视频', value: 'video' },
  { label: '图片和视频都必填', value: 'image_video' },
];

const uploadRequirementText: Record<ServiceProcessUploadRequirement, string> = {
  none: '不要求',
  image: '图片必填',
  video: '视频必填',
  image_video: '图片+视频必填',
};

const defaultSteps: ServiceProcessStep[] = [
  {
    id: 'accept_confirm',
    name: '接单确认',
    description: '确认服务时间、地点和收费',
    sort: 10,
    uploadRequirement: 'none',
    allowRemark: true,
    mustCompleteBeforeNext: true,
    enabled: true,
  },
  {
    id: 'arrival_checkin',
    name: '到达签到',
    description: '定位签到并上传现场照片',
    sort: 20,
    uploadRequirement: 'image',
    allowRemark: true,
    mustCompleteBeforeNext: true,
    enabled: true,
  },
  {
    id: 'service_execution',
    name: '服务执行',
    description: '记录喂养、清洁、遛狗等服务过程',
    sort: 30,
    uploadRequirement: 'image',
    allowRemark: true,
    mustCompleteBeforeNext: true,
    enabled: true,
  },
  {
    id: 'completion_submit',
    name: '完成提交',
    description: '上传完成履约资料，所有步骤完成后才允许提交子订单',
    sort: 40,
    uploadRequirement: 'image_video',
    allowRemark: true,
    mustCompleteBeforeNext: true,
    enabled: true,
  },
];

const createStepId = () => `step_${Date.now()}_${Math.random().toString(16).slice(2, 8)}`;

const cloneSteps = (steps: ServiceProcessStep[]) => steps.map(step => ({ ...step }));

const flattenCategories = (categories: ServiceCategory[], parentNames: string[] = []): CategoryOption[] => {
  return categories.flatMap(category => {
    const namePath = [...parentNames, category.name];
    const current: CategoryOption = {
      label: namePath.join(' / '),
      value: category.id,
      disabled: category.status !== 1,
    };
    return [current, ...flattenCategories(category.children || [], namePath)];
  });
};

const normalizeSteps = (steps: ServiceProcessStep[]) => {
  return [...steps].sort((a, b) => Number(a.sort || 0) - Number(b.sort || 0));
};

const StaffServiceProcessConfig: React.FC = () => {
  const [stepForm] = Form.useForm<ServiceProcessStep>();
  const [categoryOptions, setCategoryOptions] = useState<CategoryOption[]>([]);
  const [categoryLoading, setCategoryLoading] = useState(false);
  const [selectedCategoryId, setSelectedCategoryId] = useState('');
  const [selectedSteps, setSelectedSteps] = useState<ServiceProcessStep[]>([]);
  const [configLoading, setConfigLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingStepId, setEditingStepId] = useState('');

  const selectedCategoryLabel = useMemo(() => {
    return categoryOptions.find(item => item.value === selectedCategoryId)?.label || '未选择服务分类';
  }, [categoryOptions, selectedCategoryId]);

  const loadCategories = async () => {
    setCategoryLoading(true);
    try {
      const res = await getServiceCategoryTree();
      const options = flattenCategories(res.data || []);
      setCategoryOptions(options);
      setSelectedCategoryId(current => current || options.find(item => !item.disabled)?.value || '');
    } catch {
      message.error('加载服务分类失败');
    } finally {
      setCategoryLoading(false);
    }
  };

  useEffect(() => {
    loadCategories();
  }, []);

  const loadProcessConfig = async (categoryId: string) => {
    if (!categoryId) {
      setSelectedSteps([]);
      return;
    }

    setConfigLoading(true);
    try {
      const data = await getServiceProcessConfig(categoryId);
      setSelectedSteps(normalizeSteps(data.steps || []));
    } catch {
      setSelectedSteps([]);
      message.error('加载服务过程配置失败');
    } finally {
      setConfigLoading(false);
    }
  };

  useEffect(() => {
    void loadProcessConfig(selectedCategoryId);
  }, [selectedCategoryId]);

  const persistSteps = async (steps: ServiceProcessStep[], successText = '配置已保存') => {
    if (!selectedCategoryId) {
      message.warning('请先选择服务分类');
      return false;
    }

    setSaving(true);
    try {
      const saved = await saveServiceProcessConfig(selectedCategoryId, {
        steps: normalizeSteps(steps),
      });
      setSelectedSteps(normalizeSteps(saved.steps || []));
      message.success(successText);
      return true;
    } catch {
      message.error('保存服务过程配置失败');
      return false;
    } finally {
      setSaving(false);
    }
  };

  const openCreateModal = () => {
    if (!selectedCategoryId) {
      message.warning('请先选择服务分类');
      return;
    }

    setEditingStepId('');
    stepForm.setFieldsValue({
      id: createStepId(),
      name: '',
      description: '',
      sort: (selectedSteps.length + 1) * 10,
      uploadRequirement: 'image',
      allowRemark: true,
      mustCompleteBeforeNext: true,
      enabled: true,
    });
    setModalVisible(true);
  };

  const openEditModal = (record: ServiceProcessStep) => {
    setEditingStepId(record.id);
    stepForm.setFieldsValue(record);
    setModalVisible(true);
  };

  const handleSaveStep = async () => {
    const values = await stepForm.validateFields();
    const nextStep: ServiceProcessStep = {
      ...values,
      id: values.id || createStepId(),
      name: values.name.trim(),
      description: String(values.description || '').trim(),
      sort: Number(values.sort || 0),
      uploadRequirement: values.uploadRequirement || 'none',
      allowRemark: !!values.allowRemark,
      mustCompleteBeforeNext: !!values.mustCompleteBeforeNext,
      enabled: !!values.enabled,
    };

    const nextSteps = editingStepId
      ? selectedSteps.map(item => (item.id === editingStepId ? nextStep : item))
      : [...selectedSteps, nextStep];

    const saved = await persistSteps(nextSteps, editingStepId ? '步骤已更新' : '步骤已新增');
    if (saved) {
      setModalVisible(false);
    }
  };

  const handleDeleteStep = (id: string) => {
    void persistSteps(
      selectedSteps.filter(item => item.id !== id),
      '步骤已删除'
    );
  };

  const handleInitDefaultSteps = () => {
    void persistSteps(cloneSteps(defaultSteps), '已生成默认服务过程');
  };

  const handleClearSteps = () => {
    void persistSteps([], '已清空当前分类流程');
  };

  const columns: ColumnsType<ServiceProcessStep> = [
    {
      title: '排序',
      dataIndex: 'sort',
      key: 'sort',
      width: 90,
    },
    {
      title: '过程名称',
      dataIndex: 'name',
      key: 'name',
      width: 160,
    },
    {
      title: '过程说明',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
      render: (description: string) => description || '-',
    },
    {
      title: '上传要求',
      dataIndex: 'uploadRequirement',
      key: 'uploadRequirement',
      width: 150,
      render: (value: ServiceProcessUploadRequirement) => <Tag color={value === 'none' ? 'default' : 'blue'}>{uploadRequirementText[value]}</Tag>,
    },
    {
      title: '备注',
      dataIndex: 'allowRemark',
      key: 'allowRemark',
      width: 90,
      render: (value: boolean) => <Tag color={value ? 'green' : 'default'}>{value ? '允许' : '关闭'}</Tag>,
    },
    {
      title: '完成后下一步',
      dataIndex: 'mustCompleteBeforeNext',
      key: 'mustCompleteBeforeNext',
      width: 130,
      render: (value: boolean) => <Tag color={value ? 'orange' : 'default'}>{value ? '强制' : '不强制'}</Tag>,
    },
    {
      title: '状态',
      dataIndex: 'enabled',
      key: 'enabled',
      width: 90,
      render: (value: boolean) => <Tag color={value ? 'green' : 'red'}>{value ? '启用' : '禁用'}</Tag>,
    },
    {
      title: '操作',
      key: 'action',
      width: 160,
      render: (_, record) => (
        <Space>
          <Button size="small" icon={<EditOutlined />} onClick={() => openEditModal(record)}>
            编辑
          </Button>
          <Popconfirm title="确定删除该服务过程吗？" onConfirm={() => handleDeleteStep(record.id)}>
            <Button size="small" danger icon={<DeleteOutlined />}>
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div className="page-header">
        <Title level={2}>服务过程管理</Title>
        <Paragraph type="secondary" style={{ marginBottom: 0 }}>
          按服务分类配置服务人员小程序中的履约子过程，后续服务执行页会按这些步骤逐步完成。
        </Paragraph>
      </div>

      <div className="page-content">
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 24 }}
          title="当前先完成后台菜单和配置页面"
          description="本页面配置已接入后端数据库。后续确认字段和交互后，再接入服务小程序执行页的步骤校验与提交限制。"
        />

        <Card style={{ marginBottom: 24 }}>
          <Space wrap style={{ width: '100%', justifyContent: 'space-between' }}>
            <Space wrap>
              <Text strong>绑定服务分类</Text>
              <Select
                style={{ width: 320 }}
                loading={categoryLoading}
                options={categoryOptions}
                value={selectedCategoryId || undefined}
                placeholder="请选择服务分类"
                onChange={setSelectedCategoryId}
                showSearch
                optionFilterProp="label"
              />
              <Button icon={<ReloadOutlined />} onClick={loadCategories}>
                刷新分类
              </Button>
              <Button disabled={!selectedCategoryId} onClick={() => loadProcessConfig(selectedCategoryId)}>
                刷新流程
              </Button>
            </Space>
            <Space wrap>
              <Button onClick={handleInitDefaultSteps}>生成默认流程</Button>
              <Popconfirm title="确定清空当前分类下的服务过程吗？" onConfirm={handleClearSteps}>
                <Button danger>清空流程</Button>
              </Popconfirm>
              <Button type="primary" icon={<PlusOutlined />} onClick={openCreateModal}>
                新增过程
              </Button>
            </Space>
          </Space>
        </Card>

        <Card
          title={`流程配置：${selectedCategoryLabel}`}
          extra={
            <Space>
              <Tag color="blue">共 {selectedSteps.length} 步</Tag>
              <Button icon={<SaveOutlined />} loading={saving} disabled={!selectedCategoryId} onClick={() => persistSteps(selectedSteps)}>
                保存配置
              </Button>
            </Space>
          }
        >
          <Spin spinning={configLoading}>
            {selectedCategoryId ? (
              <Table
                columns={columns}
                dataSource={selectedSteps}
                rowKey="id"
                pagination={false}
                locale={{ emptyText: <Empty description="暂无服务过程，请新增或生成默认流程" /> }}
              />
            ) : (
              <Empty description="请先选择一个服务分类" />
            )}
          </Spin>
        </Card>
      </div>

      <Modal
        title={editingStepId ? '编辑服务过程' : '新增服务过程'}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        onOk={handleSaveStep}
        confirmLoading={saving}
        width={640}
        destroyOnHidden
      >
        <Form
          form={stepForm}
          layout="vertical"
          initialValues={{
            uploadRequirement: 'image',
            allowRemark: true,
            mustCompleteBeforeNext: true,
            enabled: true,
          }}
        >
          <Form.Item name="id" hidden>
            <Input />
          </Form.Item>
          <Form.Item name="name" label="过程名称" rules={[{ required: true, message: '请输入过程名称' }]}>
            <Input placeholder="例如：到达签到、服务执行、完成提交" maxLength={50} />
          </Form.Item>
          <Form.Item name="description" label="过程说明">
            <Input.TextArea rows={3} placeholder="填写服务人员看到的过程说明" maxLength={200} showCount />
          </Form.Item>
          <Form.Item name="sort" label="排序" rules={[{ required: true, message: '请输入排序' }]}>
            <InputNumber min={0} max={9999} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item
            name="uploadRequirement"
            label="资料上传要求"
            rules={[{ required: true, message: '请选择资料上传要求' }]}
          >
            <Select options={uploadRequirementOptions} />
          </Form.Item>
          <Form.Item name="allowRemark" label="允许填写过程备注" valuePropName="checked">
            <Switch checkedChildren="允许" unCheckedChildren="关闭" />
          </Form.Item>
          <Form.Item name="mustCompleteBeforeNext" label="完成当前过程后才能进入下一步" valuePropName="checked">
            <Switch checkedChildren="强制" unCheckedChildren="不强制" />
          </Form.Item>
          <Form.Item name="enabled" label="启用状态" valuePropName="checked">
            <Switch checkedChildren="启用" unCheckedChildren="禁用" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default StaffServiceProcessConfig;
